/**
 * Shape of the object returned by `GET user`.
 *
 * The keys are snake_case because that is what the API sends and what every
 * consumer already reads. Sections outside this block read the very same object
 * out of the session store, so these keys must NOT be renamed to camelCase -
 * doing so would silently break them.
 *
 * @typedef {Object} User
 * @property {string} username            display name, "" when unset
 * @property {string} email               account email, "" when unset
 * @property {string|null} eth_account_address  linked wallet, null when unlinked
 * @property {boolean} subscribe_to_news  news subscription preference
 */

/**
 * Session lifecycle state.
 *
 * - `unknown`          the session has not been validated against the server yet
 * - `authenticated`    a valid session cookie was confirmed
 * - `unauthenticated`  no session, or the server rejected the one we had
 *
 * @typedef {"unknown"|"authenticated"|"unauthenticated"} AuthStatus
 */

/**
 * Give the raw `GET user` payload one guaranteed shape.
 *
 * Spreads the raw object first so unknown fields survive untouched, then fills
 * defaults for the four fields the UI actually reads. `subscribe_to_news` is
 * coerced to a real boolean because the API has returned it as `null` for
 * accounts that never toggled it, and `!!null` and `null` render differently
 * through a controlled checkbox.
 *
 * @param {unknown} raw
 * @returns {User|null} null when the payload is not an object
 */
export const normalizeUser = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  return {
    ...raw,
    username: raw.username ?? "",
    email: raw.email ?? "",
    eth_account_address: raw.eth_account_address ?? null,
    subscribe_to_news: !!raw.subscribe_to_news,
  };
};
